import { DataList } from '@radix-ui/themes';
import { MAX_SHOTS_USED } from 'classes/plate-canvas';
import { MachineContext } from 'contexts/machine.context';
import { MatchingShotsContext } from 'contexts/pitch-lists/matching-shots.context';
import { EllipseHelper } from 'lib_ts/classes/ellipse.helper';
import { numberToImperial } from 'lib_ts/classes/math.utilities';
import { TrajHelper } from 'lib_ts/classes/trajectory.helper';
import { ICovarianceSummary, IEllipse } from 'lib_ts/interfaces/i-ellipses';
import { IPitch, IPlateLoc } from 'lib_ts/interfaces/pitches';
import { IMachineShot } from 'lib_ts/interfaces/training/i-machine-shot';
import { env } from 'process';
import { useContext } from 'react';
import { CommonDetails } from '../details';

/** digits after decimal to keep, like 2 */
const DEBUG_PRECISION = 2;

/** like 100 */
const DEBUG_FACTOR = Math.pow(10, DEBUG_PRECISION);

const roundPrecision = (value: number): string => {
  return (Math.round(value * DEBUG_FACTOR) / DEBUG_FACTOR).toFixed(
    DEBUG_PRECISION
  );
};

interface IDebugDetail {
  label: string;
  value: string;
}

export const Debugger = (props: {
  pitch: IPitch | undefined;
  newShot: IMachineShot | undefined;
  matching: boolean | undefined;
  testing: boolean;
  slider: IPlateLoc;
  testSlider: IPlateLoc;
  currEllipse: IEllipse | undefined;
  prevEllipse: IEllipse | undefined;
  covSummary: ICovarianceSummary | undefined;
}) => {
  const { lastMSHash } = useContext(MachineContext);
  const { safeGetShotsByPitch } = useContext(MatchingShotsContext);

  const items: IDebugDetail[] = [
    {
      label: 'Last MS Hash',
      value: lastMSHash ?? '(none)',
    },
    {
      label: 'Current X,Y (ft)',
      value: [
        roundPrecision(props.slider.plate_x),
        roundPrecision(props.slider.plate_z),
      ].join(', '),
    },
  ];

  if (props.testing) {
    items.push({
      label: 'Test X,Y (ft)',
      value: [
        roundPrecision(props.testSlider.plate_x),
        roundPrecision(props.testSlider.plate_z),
      ].join(', '),
    });

    if (props.currEllipse) {
      items.push({
        label: 'Test In Ellipse',
        value:
          EllipseHelper.checkPointInEllipse({
            origin: {
              plate_x: props.slider.plate_x,
              plate_z: props.slider.plate_z,
            },
            point: {
              plate_x: props.testSlider.plate_x,
              plate_z: props.testSlider.plate_z,
            },
            ellipse: props.currEllipse,
            factor: EllipseHelper.MAX_SCALING.factor,
            isPointRotated: true,
          }) <= 1
            ? 'In'
            : 'Out',
      });
    }
  }

  if (props.pitch && props.matching) {
    const latestShots = safeGetShotsByPitch(props.pitch);

    if (latestShots.length > 0) {
      /** list out first 5 shots' actual location */
      items.push(
        ...latestShots
          .filter((_, i) => i < MAX_SHOTS_USED)
          .map((s, i) => {
            const plate = TrajHelper.getPlateLoc(s.traj);

            return {
              label: `Recent #${i + 1}`,
              value: `${plate.plate_x.toFixed(2)}, ${plate.plate_z.toFixed(2)}`,
            };
          })
      );
    }

    if (props.newShot) {
      items.push({
        label: 'New Shot ID',
        value: props.newShot._id,
      });

      if (props.prevEllipse) {
        const newShotRawLoc = TrajHelper.getPlateLoc(props.newShot.traj);

        const whichEllipse = EllipseHelper.getEllipseContainingPoint({
          origin: {
            plate_x: props.slider.plate_x,
            plate_z: props.slider.plate_z,
          },
          point: newShotRawLoc,
          ellipse: props.prevEllipse,
          isPointRotated: true,
        });

        items.push({
          label: 'Found In',
          value: whichEllipse?.name ?? 'none',
        });
      }
    }
  }

  if (props.covSummary) {
    const mean = {
      x: numberToImperial(props.covSummary.mean_location.plate_x),
      z: numberToImperial(props.covSummary.mean_location.plate_z),
    };

    const stdev = {
      x: numberToImperial(props.covSummary.std_location.plate_x),
      z: numberToImperial(props.covSummary.std_location.plate_z),
    };

    items.push(
      {
        label: 'Rot. Mean X,Y (ft)',
        value: [
          roundPrecision(props.covSummary.mean_location.plate_x),
          roundPrecision(props.covSummary.mean_location.plate_z),
        ].join(','),
      },
      {
        label: 'approx.',
        value: `${mean.x.sign === -1 ? '-' : ''}${mean.x.ft}' ${mean.x.in}", ${
          mean.z.sign === -1 ? '-' : ''
        }${mean.z.ft}' ${mean.z.in}"`,
      },
      {
        label: 'Rot. Stn. Dev. (ft)',
        value: [
          roundPrecision(props.covSummary.std_location.plate_x),
          roundPrecision(props.covSummary.std_location.plate_z),
        ].join(', '),
      },
      {
        label: 'approx.',
        value: `${stdev.x.sign === -1 ? '-' : ''}${stdev.x.ft}' ${
          stdev.x.in
        }", ${stdev.z.sign === -1 ? '-' : ''}${stdev.z.ft}' ${stdev.z.in}"`,
      }
    );

    if (props.currEllipse && !env.production) {
      /** ellipse details */
      items.push(
        {
          label: 'Angle (rad)',
          value: `${roundPrecision(props.currEllipse.angle_radians)}`,
        },
        {
          label: 'Minor R',
          value: `${roundPrecision(props.currEllipse.minorRadius)}`,
        },
        {
          label: 'Major R',
          value: `${roundPrecision(props.currEllipse.majorRadius)}`,
        },
        {
          label: 'Eigenvector[0]',
          value: `${roundPrecision(props.currEllipse.eigVector[0])}`,
        },
        {
          label: 'Eigenvector[1]',
          value: `${roundPrecision(props.currEllipse.eigVector[1])}`,
        }
      );
    }
  }

  return (
    <CommonDetails summary="Debug">
      <DataList.Root mt="2">
        {items.map((detail, i) => (
          <DataList.Item key={`debug-detail-${i}`}>
            <DataList.Label>{detail.label}</DataList.Label>
            <DataList.Value>{detail.value}</DataList.Value>
          </DataList.Item>
        ))}
      </DataList.Root>
    </CommonDetails>
  );
};
